/**
 * @module providers/OrdersProvider
 * @description React Context Provider for order history management.
 * Provides methods to fetch, refresh, and update orders from Sanity CMS via Netlify functions.
 * Implements caching to avoid unnecessary API calls.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';

import {
  useIdentity,
  identityHeadersPromise,
} from 'src/providers/IdentityProvider';
import { get } from 'src/modules/utils';
import { Sanity } from 'src/types/sanity';
import { OrdersContextValue, OrdersProviderProps } from './types';

const debug = (window as any).debugOrders || false;

const OrdersContext = React.createContext<OrdersContextValue | null>(null);

/**
 * @component OrdersProvider
 * @summary React Context Provider for orders management
 *  - provides methods to fetch, refresh, and update orders
 *  - caches orders to avoid unnecessary API calls
 * @param {OrdersProviderProps} props component props
 * @returns {React.ReactElement} React Context provider
 */
const OrdersProvider: React.FC<OrdersProviderProps> = ({ children, ...props }) => {
  const [orders, setOrders] = useState<Sanity.Types.FrozenOrder[] | null>(null);
  const { isLoggedIn } = (useIdentity() as unknown) as { isLoggedIn: boolean };

  useEffect(() => {
    if (!isLoggedIn) {
      setOrders(null);
    }
  }, [isLoggedIn]);

  /**
   * @func getOrderHistory
   * @async
   * @summary fetch orders from Sanity via Netlify
   *  - updates state with orders (once, will short-circuit if called again)
   * @returns {Promise<Sanity.Types.FrozenOrder[] | null>} array of orders from sanity or `null` if error
   */
  const getOrderHistory = useCallback(async (): Promise<Sanity.Types.FrozenOrder[] | null> => {
    if (orders) return orders;

    debug &&
      console.log('%corders being fetched', 'background:#ffd040; color:#222;');
    const headers = await identityHeadersPromise();
    const _orders = await window
      .fetch('/.netlify/functions/get-orders', {
        method: 'GET',
        credentials: 'include',
        headers,
      })
      .then((res) => res.json())
      .then((data) => data.orders)
      .catch((err) => {
        console.error('fetch order history error', err);
        return null;
      });

    if (Array.isArray(_orders)) {
      setOrders(_orders);
    } else {
      setOrders(null);
    }

    debug && console.log('%corders', 'background:blue; color:white;', _orders);
    return _orders || null;
  }, [orders]);

  /**
   * @func refreshOrders
   * @async
   * @summary Force-refresh orders from the server and update local state
   * @returns {Promise<Sanity.Types.FrozenOrder[] | null>} fresh orders array or null
   */
  const refreshOrders = useCallback(async (): Promise<Sanity.Types.FrozenOrder[] | null> => {
    debug && console.log('%cforce refreshing orders', 'background:#ffd040; color:#222;');
    const headers = await identityHeadersPromise();
    const _orders = await window
      .fetch('/.netlify/functions/get-orders', {
        method: 'GET',
        credentials: 'include',
        headers,
      })
      .then((res) => res.json())
      .then((data) => data.orders)
      .catch((err) => {
        console.error('refresh orders error', err);
        return null;
      });

    const ordersArray = Array.isArray(_orders) ? _orders : null;
    setOrders(ordersArray);
    debug && console.log('%crefreshed orders', 'background:blue; color:white;', ordersArray);
    return ordersArray;
  }, []);

  /**
   * @func getOrder
   * @async
   * @summary search local orders or order history for entry that matches given id
   * @param {string | number} orderId id of order
   * @returns {Promise<Sanity.Types.FrozenOrder | null>} order data or null if not found
   */
  const getOrder = useCallback(
    async (orderId: string | number): Promise<Sanity.Types.FrozenOrder | null> => {
      const _orders = orders || (await getOrderHistory());

      if (!_orders) return null;
      const _order = _orders.find((order) => orderId === get(order, '_id', null));

      return _order || null;
    },
    [orders, getOrderHistory],
  );

  /**
   * @func updateOrder
   * @summary Update an existing order in local state
   * @param {string | number} orderId id of order
   * @param {Partial<Sanity.Types.FrozenOrder>} newData new data for order
   * @returns {boolean} if order successfully updated or not
   */
  const updateOrder = useCallback(
    (orderId: string | number, newData: Partial<Sanity.Types.FrozenOrder>): boolean => {
      if (!orders) return false;

      let didUpdate = false;

      const newOrders = orders.map((order) => {
        if (order._id === orderId) {
          didUpdate = true;
          return {
            ...order,
            ...newData,
          };
        }
        return order;
      });
      setOrders(newOrders);
      debug &&
        console.log(
          '%cdidUpdate?',
          'background:#bada55; color:#222;',
          didUpdate,
        );
      return didUpdate;
    },
    [orders],
  );

  /**
   * @func refreshOrder
   * @async
   * @summary Refresh orders from the server and return the specific order
   * @param {string | number} orderId id of order
   * @returns {Promise<Sanity.Types.FrozenOrder | null>} fresh order or null if not found
   */
  const refreshOrder = useCallback(
    async (orderId: string | number): Promise<Sanity.Types.FrozenOrder | null> => {
      const _orders = await refreshOrders();
      if (!_orders) return null;
      const _order = _orders.find((order) => orderId === get(order, '_id', null));
      return _order || null;
    },
    [refreshOrders],
  );

  const context: OrdersContextValue = {
    getOrderHistory,
    getOrder,
    updateOrder,
    refreshOrders,
    refreshOrder,
  };

  return (
    <OrdersContext.Provider value={context}>
      {children}
    </OrdersContext.Provider>
  );
};

/**
 * @func useOrders
 * @summary React Hook to access Orders context methods
 * @returns {OrdersContextValue} orders context value
 * @throws {Error} if used outside OrdersProvider
 */
export const useOrders = (): OrdersContextValue => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrdersProvider');
  }
  return context;
};

export default OrdersProvider;
